// UploadFiles Store 统一导出，Direct Merge 合并 taskStore 和 modalStore

import { create } from "zustand";
import type { ILoadingStatus } from "@helium10/re-core";
import type { ILoadFile } from "@helium10/re-ui-components";
import { getCurrentAccountId } from "@helium10/re-core";
import { type IUploadSingleFileResponse, type IInboundFields } from "@/features/inbound/requests";
import { useTranslationStore } from "@/i18n/translationStore";
import { callbackResult } from "../request";

// ========== 类型导出 ==========
export type { IUploadFileItem, UploadTaskStatus, IUploadTask } from "./taskStore";

export type {
  UploadModalMode,
  IUploadModalState,
  IUploadDrawerState,
  IUploadSimpleModalState,
} from "./modalStore";

// ========== 工具函数导出 ==========
export { calculateFileMD5 } from "./taskStore";

// ========== 校验常量（与 en.json fileUploadZone 一致）==========
export const MAX_SINGLE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_TOTAL_SIZE = 1024 * 1024 * 1024; // 1GB
export const MAX_FILES_COUNT = 200;

// 每个 Shipment ID 下，用户上传（type !== "system"）文件的最大数量
export const MAX_CUSTOMER_FILES_PER_SHIPMENT = 4;

// 统计已存在文件中用户上传（customer）的数量，type === "system" 不计入
export const getCustomerFilesCount = (files: { type?: string }[] | null | undefined): number =>
  files?.filter((f) => f.type !== "system").length ?? 0;

// 获取文件唯一标识，优先 webkitRelativePath/relativePath，否则 metadata.name
export const getFileKey = (file: ILoadFile): string => {
  const meta = file.metadata as File & { webkitRelativePath?: string; relativePath?: string };
  return meta?.webkitRelativePath || meta?.relativePath || file.metadata.name;
};

// ========== 导入内部类型 ==========
import type { IUploadFileItem, UploadTaskStatus, IUploadTask } from "./taskStore";
import type {
  UploadModalMode,
  IUploadModalState,
  IUploadDrawerState,
  IUploadSimpleModalState,
} from "./modalStore";

// ========== 合并后的 Store 接口 ==========
export interface IUploadFilesStore {
  tasks: Record<string, IUploadTask>;

  // ========== 任务管理方法 ==========
  getOrCreateTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => IUploadTask;
  addFiles: (taskId: string, files: ILoadFile[]) => void;
  removeFiles: (taskId: string, fileNames: string[]) => void;
  updateFileStatus: (
    taskId: string,
    fileName: string,
    status: ILoadingStatus,
    result?: IUploadFileItem["result"],
  ) => void;
  clearTask: (taskId: string) => void;
  clearBulkUploadTasks: () => void;
  resetTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => void;
  updateTaskStatus: (taskId: string, status: UploadTaskStatus) => void;
  getTask: (taskId: string) => IUploadTask | undefined;
  hasUploadingTasks: () => boolean;
  uploadFilesToS3: (
    taskId: string,
    uploadResponse: IUploadSingleFileResponse,
    onComplete?: (taskStatus: "uploaded" | "error") => void,
  ) => Promise<void>;

  // ========== 弹窗/抽屉状态（三个独立的状态空间）==========
  uploadModal: IUploadModalState;
  uploadDrawer: IUploadDrawerState;
  uploadSimpleModal: IUploadSimpleModalState;

  // ========== UploadModal 控制器方法 ==========
  openUploadModal: (data: IInboundFields) => void;
  closeUploadModal: () => void;
  setUploadModalMode: (mode: UploadModalMode) => void;
  setUploadModalConfirming: (confirming: boolean) => void;
  resetUploadModal: () => void;

  // ========== UploadDrawer 控制器方法 ==========
  openUploadDrawer: (data: any) => void;
  closeUploadDrawer: () => void;
  setUploadDrawerMode: (mode: UploadModalMode) => void;
  setUploadDrawerConfirming: (confirming: boolean) => void;
  resetUploadDrawer: () => void;

  // ========== UploadSimpleModal 控制器方法 ==========
  openUploadSimpleModal: (data: any) => void;
  closeUploadSimpleModal: () => void;
  setUploadSimpleModalConfirming: (confirming: boolean) => void;
  resetUploadSimpleModal: () => void;
}

// S3 上传工具：使用 PUT 方法上传文件到预签名 URL
const uploadToS3 = async (uploadUrl: string, fileBlob: Blob): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: fileBlob,
    headers: {
      "Content-Type": fileBlob.type || "application/octet-stream",
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
};

/**
 * 并发控制上传队列
 * 限制同时上传的文件数量为5个，避免对后端和S3造成压力
 */
class UploadQueue {
  private queue: Array<() => Promise<void>> = [];
  private running = 0;
  private maxConcurrent = 5;

  async add(task: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          await task();
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          this.running--;
          this.processQueue();
        }
      });
      this.processQueue();
    });
  }

  private processQueue() {
    while (this.running < this.maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift();
      if (task) {
        this.running++;
        task();
      }
    }
  }
}

/**
 * UploadFiles 统一 Store
 * 方案B: Direct Merge - 在一个 create 函数中直接合并两个 store 的逻辑
 */
export const useUploadFilesStore = create<IUploadFilesStore>((set, get) => {
  return {
    tasks: {},

    // ========== 弹窗/抽屉状态初始化 ==========
    uploadModal: {
      isOpen: false,
      mode: "upload",
      isConfirming: false,
      currentData: null,
    },
    uploadDrawer: {
      isOpen: false,
      mode: "upload",
      isConfirming: false,
      currentData: null,
    },
    uploadSimpleModal: {
      isOpen: false,
      isConfirming: false,
      currentData: null,
    },

    // ========== UploadModal 控制器方法 ==========
    openUploadModal: (data: IInboundFields) => {
      set((state) => ({
        uploadModal: {
          isOpen: true,
          mode: "upload",
          isConfirming: false,
          currentData: data,
        },
      }));
    },

    closeUploadModal: () => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          isOpen: false,
          currentData: null,
        },
      }));
    },

    setUploadModalMode: (mode: UploadModalMode) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          mode,
        },
      }));
    },

    setUploadModalConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          isConfirming: confirming,
        },
      }));
    },

    resetUploadModal: () => {
      set({
        uploadModal: {
          isOpen: false,
          mode: "upload",
          isConfirming: false,
          currentData: null,
        },
      });
    },

    // ========== UploadDrawer 控制器方法 ==========
    openUploadDrawer: (data: any) => {
      set((state) => ({
        uploadDrawer: {
          isOpen: true,
          mode: "upload",
          isConfirming: false,
          currentData: data,
        },
      }));
    },

    closeUploadDrawer: () => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          isOpen: false,
          currentData: null,
        },
      }));
    },

    setUploadDrawerMode: (mode: UploadModalMode) => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          mode,
        },
      }));
    },

    setUploadDrawerConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          isConfirming: confirming,
        },
      }));
    },

    resetUploadDrawer: () => {
      set({
        uploadDrawer: {
          isOpen: false,
          mode: "upload",
          isConfirming: false,
          currentData: null,
        },
      });
    },

    // ========== UploadSimpleModal 控制器方法 ==========
    openUploadSimpleModal: (data: any) => {
      set((state) => ({
        uploadSimpleModal: {
          isOpen: true,
          isConfirming: false,
          currentData: data,
        },
      }));
    },

    closeUploadSimpleModal: () => {
      set((state) => ({
        uploadSimpleModal: {
          ...state.uploadSimpleModal,
          isOpen: false,
          currentData: null,
        },
      }));
    },

    setUploadSimpleModalConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadSimpleModal: {
          ...state.uploadSimpleModal,
          isConfirming: confirming,
        },
      }));
    },

    resetUploadSimpleModal: () => {
      set({
        uploadSimpleModal: {
          isOpen: false,
          isConfirming: false,
          currentData: null,
        },
      });
    },

    // ========== 任务管理方法 ==========
    getOrCreateTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => {
      const state = get();
      const existingTask = state.tasks[taskId];
      if (existingTask) {
        return existingTask;
      }

      const newTask: IUploadTask = {
        taskId,
        fileList: [],
        status: "init",
        sellerInfo,
      };

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: newTask,
        },
      }));

      return newTask;
    },

    addFiles: (taskId: string, files: ILoadFile[]) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      const existingKeys = new Set(task.fileList.map((f) => getFileKey(f.file)));
      const newFiles = files.filter((f) => !existingKeys.has(getFileKey(f)));

      const newFileItems: IUploadFileItem[] = newFiles.map((file) => ({
        file,
        status: (file as any)?.error ? "error" : "init",
        result: (file as any)?.error
          ? {
              fileName: file.metadata.name,
              uploadUrl: "",
              error: (file as any)?.error,
            }
          : undefined,
      }));

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: [...task.fileList, ...newFileItems],
          },
        },
      }));
    },

    removeFiles: (taskId: string, fileKeys: string[]) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      const keySet = new Set(fileKeys);
      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: task.fileList.filter((item) => !keySet.has(getFileKey(item.file))),
          },
        },
      }));
    },

    updateFileStatus: (
      taskId: string,
      fileKey: string,
      status: ILoadingStatus,
      result?: IUploadFileItem["result"],
    ) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: task.fileList.map((item) =>
              getFileKey(item.file) === fileKey ? { ...item, status, result } : item,
            ),
          },
        },
      }));
    },

    clearTask: (taskId: string) => {
      set((state) => {
        const { [taskId]: removed, ...rest } = state.tasks;
        return { tasks: rest };
      });
    },

    clearBulkUploadTasks: () => {
      set((state) => {
        const rest = Object.fromEntries(
          Object.entries(state.tasks).filter(
            ([taskId]) => !taskId.startsWith("document-center-batch-"),
          ),
        );
        return { tasks: rest };
      });
    },

    resetTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => {
      const state = get();
      const task = state.tasks[taskId];

      if (!task) {
        if (!sellerInfo) {
          return;
        }
        get().getOrCreateTask(taskId, sellerInfo);
        return;
      }

      if (task.status === "uploading") {
        return;
      }

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: [],
            status: "init",
          },
        },
      }));
    },

    updateTaskStatus: (taskId: string, status: UploadTaskStatus) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            status,
          },
        },
      }));
    },

    getTask: (taskId: string) => {
      return get().tasks[taskId];
    },

    hasUploadingTasks: () => {
      const tasks = get().tasks;
      return Object.values(tasks).some((task) => task.status === "uploading");
    },

    uploadFilesToS3: async (
      taskId: string,
      uploadResponse: IUploadSingleFileResponse,
      onComplete?: (taskStatus: "uploaded" | "error") => void,
    ) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task || task.fileList.length === 0) return;

      const accountId = getCurrentAccountId() || "";

      get().updateTaskStatus(taskId, "uploading");

      task.fileList.forEach((item) => {
        if (item.status === "init") {
          get().updateFileStatus(taskId, getFileKey(item.file), "loading");
        }
      });

      try {
        if (!uploadResponse.success || !uploadResponse.data.uploadResultDtoList) {
          task.fileList.forEach((item) => {
            get().updateFileStatus(taskId, getFileKey(item.file), "error", {
              fileName: item.file.metadata.name,
              uploadUrl: "",
              error: uploadResponse.message || "Failed to get upload URL",
            });
          });
          get().updateTaskStatus(taskId, "error");
          throw new Error(uploadResponse.message || "Failed to get upload URL");
        }

        const fileMap = new Map<string, ILoadFile>();
        task.fileList.forEach((item) => {
          fileMap.set(getFileKey(item.file), item.file);
        });

        const uploadQueue = new UploadQueue();
        const uploadPromises = uploadResponse.data.uploadResultDtoList.map((result) => {
          const file = fileMap.get(result.filePath);
          if (!file) {
            return Promise.resolve();
          }

          const matchResultsWithUrl = (result.matchResult || []).filter((m) => m?.uploadUrl);
          if (matchResultsWithUrl.length === 0) {
            const matches = result.matchResult || [];
            const translation = useTranslationStore.getState().translation;

            // --- 基准：重复上传 — matchResult 中任一条 success === true 且无预签名 URL（uploadUrl 用 ?.trim?.() 判空，避免异常崩溃）---
            const isDuplicateUpload = matches.some(
              (m) => m.success === true && !m.uploadUrl?.trim?.(),
            );

            let errorMessage: string;
            if (isDuplicateUpload) {
              errorMessage =
                translation?.t("inbound.modal.upload.duplicateFileUploaded") ||
                "Duplicate file uploaded.";
            } else {
              // --- 其余「无可用 uploadUrl」：优先展示后端 failReason，否则用 i18n 兜底 ---
              const failReasonText = matches[0]?.failReason?.trim?.();
              if (failReasonText) {
                errorMessage = failReasonText;
              } else {
                errorMessage =
                  translation?.t("inbound.modal.upload.noUploadUrlReturned") ||
                  "No upload URL returned";
              }
            }

            get().updateFileStatus(taskId, getFileKey(file), "error", {
              fileName: result.fileName,
              uploadUrl: "",
              error: errorMessage,
            });
            return Promise.resolve();
          }

          return uploadQueue.add(async () => {
            let allSuccess = true;
            for (const matchResult of matchResultsWithUrl) {
              let uploadSuccess = false;
              try {
                await uploadToS3(matchResult.uploadUrl, file.blob);
                uploadSuccess = true;
              } catch (error) {
                allSuccess = false;
              }
              try {
                await callbackResult({
                  accountId,
                  batchId: uploadResponse.data.batchId,
                  filePath: result.filePath,
                  fileHash: result.fileHash,
                  sellerId: matchResult.sellerId,
                  shipmentId: matchResult.shipmentId,
                  storagePath: matchResult.storagePath,
                  uploadResult: uploadSuccess,
                  fileId: result.fileId,
                });
              } catch {
                // callbackResult 调用失败忽略
              }
            }

            if (allSuccess) {
              get().updateFileStatus(taskId, getFileKey(file), "loaded", {
                fileName: result.fileName,
                uploadUrl: matchResultsWithUrl[0].uploadUrl,
              });
            } else {
              get().updateFileStatus(taskId, getFileKey(file), "error", {
                fileName: result.fileName,
                uploadUrl: "",
                error: "Upload failed",
              });
            }
          });
        });

        await Promise.allSettled(uploadPromises);

        const updatedTask = get().tasks[taskId];
        if (!updatedTask) {
          return;
        }

        const hasError = updatedTask.fileList.some((item) => item.status === "error");
        const taskStatus: "uploaded" | "error" = hasError ? "error" : "uploaded";

        get().updateTaskStatus(taskId, taskStatus);
        onComplete?.(taskStatus);
      } catch (error) {
        get().updateTaskStatus(taskId, "error");
        throw error;
      }
    },
  };
});
